import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './book.css';
import Loader from '../components/layout/preloader';
import Header from '../components/layout/header';
import Footer from '../components/layout/footer';
import asy from '../asserts/astb.png';
import { supabase } from '../supabaseClient';

gsap.registerPlugin(ScrollTrigger);

const Book = () => {
  const PRELOADER = 4.1;

  const [visible, setVisible] = useState(false);
  const [ticketType, setTicketType] = useState('');

  const [ticketTiers, setTicketTiers] = useState([]);
  const [policyBullets, setPolicyBullets] = useState([]);
  const [eventDetails, setEventDetails] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedExhibitor, setSelectedExhibitor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const sectionRef = useRef();
  const leftRef = useRef();
  const titleRef = useRef();
  const textRef = useRef();
  const bulletsRef = useRef();
  const cardBackRef = useRef();
  const formCardRef = useRef();
  const asteriskRef = useRef();
  const ticketOptRef = useRef();
  const submitRef = useRef();
  const includedRef = useRef();
  const incTitleRef = useRef();
  const incCardsRef = useRef();
  const incInfoRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      const [tiersRes, policiesRes, detailsRes, exhibitorsRes] = await Promise.all([
        supabase.from('ticket_tiers').select('*').order('display_order'),
        supabase.from('booking_policies').select('*').order('display_order'),
        supabase.from('event_details').select('*').order('display_order'),
        supabase.from('exhibitors').select('id, name'),
      ]);
      setTicketTiers(tiersRes.data || []);
      setPolicyBullets(policiesRes.data || []);
      setEventDetails(detailsRes.data || []);
      setExhibitors(exhibitorsRes.data || []);
      if (tiersRes.data?.length > 0) setTicketType(tiersRes.data[0].tier_name_en);
      setDataReady(true);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {
      if (sectionRef.current)
        gsap.fromTo(sectionRef.current, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.out', delay: PRELOADER });

      if (titleRef.current)
        gsap.fromTo(titleRef.current,
          { x: -100, opacity: 0, skewX: -8, letterSpacing: '14px' },
          { x: 0, opacity: 1, skewX: 0, letterSpacing: '2.639px', duration: 1.1, ease: 'expo.out', delay: PRELOADER + 0.15 });

      if (textRef.current)
        gsap.fromTo(textRef.current,
          { opacity: 0, y: 30, filter: 'blur(6px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out', delay: PRELOADER + 0.35 });

      gsap.fromTo('.book-bullets li',
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.5, stagger: 0.12, ease: 'power2.out', delay: PRELOADER + 0.55 });

      if (cardBackRef.current)
        gsap.fromTo(cardBackRef.current,
          { scaleX: 0, opacity: 0, transformOrigin: 'top center' },
          { scaleX: 1, opacity: 1, duration: 1, ease: 'elastic.out(1, 0.6)', delay: PRELOADER + 0.25 });

      if (formCardRef.current)
        gsap.fromTo(formCardRef.current,
          { y: 140, opacity: 0, scale: 0.88 },
          { y: 0, opacity: 1, scale: 1, duration: 1.2, ease: 'expo.out', delay: PRELOADER + 0.4 });

      if (asteriskRef.current) {
        gsap.fromTo(asteriskRef.current,
          { rotation: -180, scale: 0, opacity: 0 },
          { rotation: 0, scale: 1, opacity: 1, duration: 1, ease: 'elastic.out(1, 0.5)', delay: PRELOADER + 0.6 });
        gsap.to(asteriskRef.current, { rotation: 360, duration: 8, repeat: -1, ease: 'none', delay: PRELOADER + 1.2 });
      }

      gsap.fromTo('.book-field',
        { opacity: 0, y: 20, filter: 'blur(4px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.5, stagger: 0.1, ease: 'power2.out', delay: PRELOADER + 0.75 });

      gsap.fromTo('.book-ticket-btn',
        { opacity: 0, x: 40 },
        { opacity: 1, x: 0, duration: 0.5, stagger: 0.1, ease: 'back.out(1.5)', delay: PRELOADER + 0.95 });

      if (submitRef.current)
        gsap.fromTo(submitRef.current,
          { scale: 0.5, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.7, ease: 'elastic.out(1, 0.4)', delay: PRELOADER + 1.2 });

      if (incTitleRef.current)
        gsap.fromTo(incTitleRef.current,
          { opacity: 0, y: 40, letterSpacing: '16px' },
          { opacity: 1, y: 0, letterSpacing: '2.294px', duration: 1, ease: 'expo.out', scrollTrigger: { trigger: includedRef.current, start: 'top 78%' } });

      gsap.fromTo('.included-card',
        { opacity: 0, y: 60, scale: 0.85 },
        { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.15, ease: 'back.out(1.6)', scrollTrigger: { trigger: incCardsRef.current, start: 'top 78%' } });

      if (incInfoRef.current)
        gsap.fromTo(incInfoRef.current,
          { opacity: 0, y: 50, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'power3.out', scrollTrigger: { trigger: incInfoRef.current, start: 'top 80%' } });

      const svgPath = document.querySelector('.included-svg-bg path');
      if (svgPath) {
        const len = svgPath.getTotalLength?.() || 4000;
        gsap.fromTo(svgPath,
          { strokeDasharray: len, strokeDashoffset: len, opacity: 0 },
          { strokeDashoffset: 0, opacity: 1, duration: 2.5, ease: 'power2.inOut', scrollTrigger: { trigger: includedRef.current, start: 'top 75%' } });
      }
    });

    return () => ctx.revert();
  }, [dataReady]);

  const selectedTier = ticketTiers.find(t => t.tier_name_en === ticketType);
  const unitPrice = selectedTier?.price_amount ?? 0;
  const totalPrice = unitPrice * quantity;

  const handleQuantityChange = (delta) =>
    setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));

  const handleBooking = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setErrorMessage('');

    // 1. Insert user
    const { data: userData, error: userError } = await supabase
      .from('users')
      .insert([{ full_name: fullName, email, role: 'User', status: 'UNVERIFIED' }])
      .select('id')
      .single();

    if (userError) {
      setErrorMessage('User error: ' + userError.message);
      setSubmitStatus('error');
      return;
    }

    // 2. Insert booking — exhibitor_id included here
    const { error: bookingError } = await supabase
      .from('booking')
      .insert([{
        user_name: fullName,
        total_paid: totalPrice,
        payment_status: 'PENDING',
        user_id: userData.id,
        ticket_tier_id: selectedTier?.id ?? null,
        quantity,
        exhibitor_id: selectedExhibitor || null,   // ← THIS WAS MISSING
      }]);

    if (bookingError) {
      setErrorMessage('Booking error: ' + bookingError.message);
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('success');
    setFullName('');
    setEmail('');
    setSelectedExhibitor('');
    setQuantity(1);
    if (ticketTiers.length > 0) setTicketType(ticketTiers[0].tier_name_en);
  };

  const getDetail = (title) => eventDetails.find(d => d.title_en === title);

  return (
    <>
      <Loader />
      <Header />

      <main>
        <section
          ref={sectionRef}
          className={`book-section${visible ? ' book-visible' : ''} gsap-hidden`}
        >
          <div className="book-wrapper">

            <div className="book-left" ref={leftRef}>
              <h2 className="book-title" ref={titleRef}>BOOK YOUR TICKET</h2>
              <p className="book-text" ref={textRef}>
                By booking your ticket, you agree to our terms and conditions regarding event access, data usage, and privacy protection.
                We are committed to ensuring that all personal information collected during the booking process is handled securely and in
                accordance with applicable data protection regulations. Your details will only be used for event-related communication,
                ticket verification, and improving your overall experience.
              </p>
              <p className="book-subtext">POLICY HIGHLIGHTS</p>
              <ul className="book-bullets" ref={bulletsRef}>
                {policyBullets.map((item) => (
                  <li key={item.id}>{item.point_text_en}</li>
                ))}
              </ul>
            </div>

            <div className="book-right">
              <div className="book-card-back" ref={cardBackRef}></div>
              <div className="book-form-card" ref={formCardRef}>

                <img src={asy} alt="Decorative asterisk" className="asy" ref={asteriskRef} />

                <form className='fform' onSubmit={handleBooking}>

                  <div className="book-field">
                    <label className="book-label" htmlFor="fullName">
                      <svg className="book-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M8 8C9.933 8 11.5 6.433 11.5 4.5S9.933 1 8 1 4.5 2.567 4.5 4.5 6.067 8 8 8z" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 14.5c0-3.038 2.686-5.5 6-5.5s6 2.462 6 5.5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      FULL NAME
                    </label>
                    <input id="fullName" className="book-input" type="text" placeholder="Enter your full name"
                      value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                  </div>

                  <div className="book-field">
                    <label className="book-label" htmlFor="emailAddress">
                      <svg className="book-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2" />
                        <path d="M1.5 3.5l6.5 5 6.5-5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                      EMAIL ADDRESS
                    </label>
                    <input id="emailAddress" className="book-input" type="email" placeholder="your-email@example.com"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>

                  <div className="book-field">
                    <label className="book-label" htmlFor="exhibitorSelect">
                      <svg className="book-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2" />
                        <path d="M4 6h8M4 9.5h5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                      EXHIBITOR
                    </label>
                    <div className="book-select-wrapper">
                      <select id="exhibitorSelect" className="book-input book-select"
                        value={selectedExhibitor} onChange={(e) => setSelectedExhibitor(e.target.value)}>
                        <option value="">Select an exhibitor</option>
                        {exhibitors.map((ex) => (
                          <option key={ex.id} value={ex.id}>{ex.name}</option>
                        ))}
                      </select>
                      <svg className="book-select-arrow" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                        <path d="M1 1l5 5 5-5" stroke="#FF1E1E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>

                  <div className="book-ticket-section" ref={ticketOptRef}>
                    <p className="book-ticket-label">TICKET TYPE</p>
                    <div className="book-ticket-options">
                      {ticketTiers.map((tier) => (
                        <button key={tier.id} type="button"
                          className={`book-ticket-btn${ticketType === tier.tier_name_en ? ' active' : ''}`}
                          onClick={() => { setTicketType(tier.tier_name_en); setQuantity(1); }}>
                          <span>{tier.tier_name_en}</span>
                          <span className="ticket-price">{tier.price_amount.toLocaleString()} {tier.currency}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedTier && (
                    <div className="book-quantity-section">
                      <div className="book-quantity-row">
                        <div className="book-quantity-label-wrap">
                          <svg className="book-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <rect x="1" y="3" width="14" height="11" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2" />
                            <path d="M5 3V2a3 3 0 016 0v1" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" />
                            <circle cx="8" cy="9" r="1.5" stroke="#FF1E1E" strokeWidth="1.2" />
                          </svg>
                          <span className="book-ticket-label">QUANTITY</span>
                        </div>
                        <div className="book-qty-controls">
                          <button type="button" className="book-qty-btn" onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>
                            <svg viewBox="0 0 12 12" fill="none"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                          </button>
                          <span className="book-qty-value">{quantity}</span>
                          <button type="button" className="book-qty-btn" onClick={() => handleQuantityChange(1)} disabled={quantity >= 10}>
                            <svg viewBox="0 0 12 12" fill="none"><path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                          </button>
                        </div>
                      </div>
                      <div className="book-total-row">
                        <span className="book-total-label">TOTAL</span>
                        <span className="book-total-value">
                          {totalPrice.toLocaleString()} <span className="book-total-currency">{selectedTier.currency}</span>
                        </span>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'success' && (
                    <p className="book-note book-note--success">Booking confirmed! Check your email for details.</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="book-note book-note--error">{errorMessage || 'Something went wrong. Please try again.'}</p>
                  )}

                  <div ref={submitRef}>
                    <button type="submit" className="book-submit" disabled={submitStatus === 'loading'}>
                      {submitStatus === 'loading' ? 'CONFIRMING...' : 'CONFIRM BOOKING'}
                    </button>
                  </div>

                </form>

                <p className="book-note">You will receive a confirmation email with your ticket details</p>
              </div>
            </div>

          </div>
        </section>

        <section className="included-section" ref={includedRef}>
          <div className="included-svg-bg" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1136 856" fill="none" preserveAspectRatio="none">
              <g filter="url(#inc_filter)">
                <path d="M0 838H1123V0C1123 0 780.779 75.7046 561.5 75.7046C342.221 75.7046 0 0 0 0V838Z" fill="#1E1E1E" />
                <path d="M1112.2 838V836.5H1121.5V826.974H1123V804.921H1121.5V782.868H1123V760.815H1121.5V738.763H1123V716.71H1121.5V694.658H1123V672.605H1121.5V650.553H1123V628.5H1121.5V606.447H1123V584.395H1121.5V562.342H1123V540.29H1121.5V518.237H1123V496.185H1121.5V474.132H1123V452.079H1121.5V430.026H1123V407.974H1121.5V385.921H1123V363.868H1121.5V341.815H1123V319.763H1121.5V297.711H1123V275.658H1121.5V253.605H1123V231.553H1121.5V209.5H1123V187.447H1121.5V165.395H1123V143.342H1121.5V121.289H1123V99.2373H1121.5V77.1846H1123V55.1318H1121.5V33.0791H1123V11.0264H1121.5V1.86426C1121.24 1.92126 1120.95 1.98403 1120.63 2.05273C1119.75 2.24519 1118.65 2.48295 1117.35 2.76367L1112.83 3.73242L1112.52 2.26562C1107.2 3.3987 1099.96 4.92582 1091.04 6.76465L1091.35 8.23438C1085.04 9.53445 1077.9 10.9906 1070.02 12.5742L1069.72 11.1035C1063.09 12.4345 1055.93 13.8549 1048.29 15.3496L1048.58 16.8223C1041.81 18.1482 1034.65 19.5321 1027.16 20.9619L1026.88 19.4883C1019.98 20.804 1012.8 22.1582 1005.36 23.543L1005.63 25.0186C998.682 26.3115 991.505 27.631 984.124 28.9688L983.857 27.4922C976.881 28.7564 969.723 30.0358 962.402 31.3271L962.664 32.8066C955.642 34.0454 948.469 35.2934 941.163 36.5459L940.909 35.0654C933.853 36.2749 926.674 37.4871 919.386 38.6992L919.633 40.1826C912.546 41.3612 905.356 42.5382 898.078 43.71L897.84 42.2266C890.73 43.371 883.538 44.5132 876.272 45.6426L876.503 47.124C869.37 48.2329 862.167 49.3327 854.908 50.4189L854.687 48.9346C847.535 50.0045 840.33 51.0614 833.081 52.1016L833.295 53.5879C826.123 54.6171 818.908 55.6295 811.663 56.6221L811.46 55.1357C804.275 56.12 797.06 57.0845 789.826 58.0254L790.021 59.5127C782.815 60.4499 775.591 61.364 768.358 62.251L768.176 60.7607C760.953 61.6464 753.724 62.5055 746.497 63.334L746.669 64.8252C739.424 65.6557 732.182 66.4558 724.954 67.2217L724.796 65.7295C717.535 66.4987 710.29 67.2321 703.07 67.9287L703.216 69.4238C695.933 70.1266 688.675 70.7906 681.455 71.4121L681.326 69.917C674.028 70.5452 666.769 71.1306 659.56 71.668L659.673 73.1641C652.35 73.7099 645.077 74.2062 637.869 74.6504L637.777 73.1533C630.438 73.6056 623.166 74.0016 615.975 74.3398L616.046 75.8398C608.677 76.1864 601.391 76.4706 594.203 76.6885L594.157 75.1875C586.771 75.4114 579.489 75.5656 572.326 75.6436L572.344 77.1445C568.698 77.1842 565.083 77.2041 561.5 77.2041C557.917 77.2041 554.302 77.1842 550.656 77.1445L550.673 75.6436C543.51 75.5656 536.228 75.4114 528.843 75.1875L528.797 76.6885C521.609 76.4706 514.323 76.1864 506.954 75.8398L507.024 74.3398C499.833 74.0016 492.561 73.6056 485.223 73.1533L485.131 74.6504C477.923 74.2062 470.65 73.7099 463.327 73.1641L463.438 71.668C456.23 71.1306 448.971 70.5451 441.674 69.917L441.545 71.4121C434.325 70.7906 427.067 70.1266 419.784 69.4238L419.929 67.9287C412.709 67.2321 405.464 66.4987 398.204 65.7295L398.046 67.2217C390.818 66.4558 383.576 65.6557 376.331 64.8252L376.502 63.334C369.275 62.5055 362.046 61.6464 354.824 60.7607L354.642 62.251C347.409 61.364 340.185 60.4499 332.979 59.5127L333.173 58.0254C325.939 57.0845 318.724 56.12 311.54 55.1357L311.336 56.6221C304.091 55.6295 296.877 54.617 289.705 53.5879L289.918 52.1016C282.669 51.0614 275.464 50.0045 268.313 48.9346L268.092 50.4189C260.833 49.3327 253.629 48.2329 246.496 47.124L246.727 45.6416C239.462 44.5122 232.269 43.371 225.16 42.2266L224.922 43.71C217.644 42.5382 210.454 41.3612 203.367 40.1826L203.613 38.6992C196.325 37.4871 189.146 36.2749 182.091 35.0654L181.837 36.5459C174.531 35.2934 167.358 34.0454 160.336 32.8066L160.596 31.3271C153.275 30.0358 146.118 28.7564 139.143 27.4922L138.875 28.9688C131.494 27.631 124.318 26.3115 117.368 25.0186L117.643 23.543C110.199 22.1582 103.016 20.804 96.1211 19.4883L95.8398 20.9619C88.347 19.532 81.194 18.1482 74.418 16.8223L74.7061 15.3496C67.068 13.855 59.9082 12.4345 53.2783 11.1035L52.9824 12.5742C45.0943 10.9907 37.9555 9.53442 31.6533 8.23438L31.957 6.76465C23.0429 4.9258 15.803 3.39868 10.4824 2.26367L10.1699 3.73242C6.75967 3.00493 4.13666 2.43765 2.36621 2.05273C2.05021 1.98403 1.76133 1.92126 1.5 1.86426V11.0264H0V33.0791H1.5V55.1318H0V77.1846H1.5V99.2373H0V121.29H1.5V143.342H0V165.395H1.5V187.447H0V209.5H1.5V231.553H0V253.605H1.5V275.658H0V297.71H1.5V319.763H0V341.815H1.5V363.868H0V385.921H1.5V407.974H0V430.026H1.5V452.079H0V474.132H1.5V496.185H0V518.237H1.5V540.289H0V562.342H1.5V584.395H0V606.447H1.5V628.5H0V650.553H1.5V672.605H0V694.658H1.5V716.711H0V738.763H1.5V760.815H0V782.868H1.5V804.921H0V826.974H1.5V836.5H10.7979V838H32.3945V836.5H53.9912V838H75.5869V836.5H97.1836V838H118.779V836.5H140.375V838H161.972V836.5H183.567V838H205.164V836.5H226.76V838H248.356V836.5H269.952V838H291.548V836.5H313.145V838H334.74V836.5H356.337V838H377.933V836.5H399.529V838H421.125V836.5H442.721V838H464.317V836.5H485.913V838H507.51V836.5H529.105V838H550.702V836.5H572.298V838H593.895V836.5H615.49V838H637.086V836.5H658.683V838H680.278V836.5H701.875V838H723.471V836.5H745.067V838H766.663V836.5H788.26V838H809.855V836.5H831.452V838H853.048V836.5H874.645V838H896.24V836.5H917.837V838H939.433V836.5H961.029V838H982.625V836.5H1004.22V838H1025.82V836.5H1047.41V838H1069.01V836.5H1090.61V838H1112.2Z" stroke="#FF1E1E" strokeWidth="3" strokeDasharray="22 22" />
              </g>
              <defs>
                <filter id="inc_filter" x="0" y="0" width="1136" height="856" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dx="9" dy="14" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                </filter>
              </defs>
            </svg>
          </div>

          <div className="included-inner">
            <div className="included-header">
              <h2 className="included-title" ref={incTitleRef}>What's Included</h2>
              <p className="included-subtitle">Your ticket grants you access to an unforgettable experience</p>
            </div>

            <div className="included-cards" ref={incCardsRef}>
              {getDetail('EVENT DATES') && (
                <div className="included-card">
                  <div className="included-card-icon">
                    <svg viewBox="0 0 38 38" fill="none"><rect x="5" y="7" width="28" height="26" rx="3" stroke="#F5E7C6" strokeWidth="1.5" /><path d="M5 14h28" stroke="#F5E7C6" strokeWidth="1.5" strokeLinecap="round" /><path d="M13 4v6M25 4v6" stroke="#F5E7C6" strokeWidth="1.5" strokeLinecap="round" /><circle cx="12" cy="22" r="1.5" fill="#F5E7C6" /><circle cx="19" cy="22" r="1.5" fill="#F5E7C6" /><circle cx="26" cy="22" r="1.5" fill="#F5E7C6" /></svg>
                  </div>
                  <p className="included-card-title">{getDetail('EVENT DATES').title_en}</p>
                  <p className="included-card-text">{getDetail('EVENT DATES').content_en}</p>
                </div>
              )}
              {getDetail('LOCATION') && (
                <div className="included-card">
                  <div className="included-card-icon">
                    <svg viewBox="0 0 38 38" fill="none"><path d="M19 4C13.477 4 9 8.477 9 14c0 8 10 20 10 20s10-12 10-20c0-5.523-4.477-10-10-10z" stroke="#F5E7C6" strokeWidth="1.5" /><circle cx="19" cy="14" r="3.5" stroke="#F5E7C6" strokeWidth="1.5" /></svg>
                  </div>
                  <p className="included-card-title">{getDetail('LOCATION').title_en}</p>
                  <p className="included-card-text">{getDetail('LOCATION').content_en}</p>
                </div>
              )}
              {getDetail('EVENT HOURS') && (
                <div className="included-card">
                  <div className="included-card-icon">
                    <svg viewBox="0 0 38 38" fill="none"><circle cx="19" cy="19" r="13" stroke="#F5E7C6" strokeWidth="1.5" /><path d="M19 11v8l5 3" stroke="#F5E7C6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                  <p className="included-card-title">{getDetail('EVENT HOURS').title_en}</p>
                  <p className="included-card-text">{getDetail('EVENT HOURS').content_en}</p>
                </div>
              )}
              {getDetail('INCLUDED') && (
                <div className="included-card">
                  <div className="included-card-icon">
                    <svg viewBox="0 0 38 38" fill="none"><rect x="7" y="12" width="24" height="20" rx="2" stroke="#F5E7C6" strokeWidth="1.5" /><path d="M13 12V9a6 6 0 0112 0v3" stroke="#F5E7C6" strokeWidth="1.5" strokeLinecap="round" /><circle cx="19" cy="22" r="2.5" stroke="#F5E7C6" strokeWidth="1.5" /></svg>
                  </div>
                  <p className="included-card-title">{getDetail('INCLUDED').title_en}</p>
                  <p className="included-card-text">{getDetail('INCLUDED').content_en}</p>
                </div>
              )}
            </div>

            <div className="included-info" ref={incInfoRef}>
              <p className="included-info-title">Important Information</p>
              <div className="included-info-cols">
                {getDetail('ADMISSION POLICY') && (
                  <div>
                    <p className="included-info-col-title">{getDetail('ADMISSION POLICY').title_en}</p>
                    <p className="included-info-col-text">{getDetail('ADMISSION POLICY').content_en}</p>
                  </div>
                )}
                {getDetail('WHAT TO BRING') && (
                  <div>
                    <p className="included-info-col-title">{getDetail('WHAT TO BRING').title_en}</p>
                    <p className="included-info-col-text">{getDetail('WHAT TO BRING').content_en}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Book;